<template>
  <div class="pdf-preview-list-page">
    <div class="pdf-preview-header">
      <a-button @click="goBack" type="link" class="back-btn">
        <a-icon type="arrow-left" /> 返回
      </a-button>
      <span class="pdf-title">{{ currentLabel }}</span>
    </div>
    <div class="pdf-preview-body">
      <div class="main-container">
        <div v-if="loading" class="loading-container">
          <a-spin tip="正在加载PDF..." />
        </div>
        <div v-else-if="error" class="error-container">
          <a-alert :message="error" type="error" show-icon />
        </div>
        <div v-else-if="pdfUrl" class="preview-content">
          <PdfPreview :url="pdfUrl" :key="pdfUrl" :auto-fit="autoFit" />
        </div>
        <div v-else class="empty-container">
          <a-empty description="请选择右侧图纸查看预览" />
        </div>
      </div>
      <div class="drawing-panel">
        <div class="drawing-panel-title">
          <span>图纸列表</span>
          <div class="page-nav">
            <a-button size="small" :disabled="activeIndex <= 0" @click="handlePrev">上一页</a-button>
            <a-button size="small" :disabled="activeIndex >= drawingList.length - 1" @click="handleNext">下一页</a-button>
          </div>
        </div>
        <div class="drawing-list" ref="drawingList">
          <div
            v-for="(item, index) in drawingList"
            :key="index"
            class="drawing-item"
            :class="{ active: activeIndex === index }"
            @click="handleClick(item, index)"
          >
            <a-icon type="file-pdf" class="drawing-icon" />
            <span class="drawing-name">{{ item.name || '第' + item.pageNumber + '页' }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PdfPreview from '@/components/pdf-preview'
import { queryFileById } from '@/api/project-execute/result'

export default {
  name: 'PdfPreviewListPage',
  components: { PdfPreview },
  data() {
    return {
      drawingList: [],
      activeIndex: -1,
      pdfUrl: '',
      loading: false,
      error: null,
      autoFit: false,
    }
  },
  computed: {
    currentLabel() {
      const item = this.drawingList[this.activeIndex]
      if (!item) return 'PDF预览'
      return item.name || '第' + item.pageNumber + '页'
    },
  },
  created() {
    try {
      const list = JSON.parse(this.$route.query.list || '[]')
      this.drawingList = list
    } catch {
      this.drawingList = []
    }
    const previewAttachment = this.$route.query.previewAttachment
    if (previewAttachment) {
      const idx = this.drawingList.findIndex(d => d.previewAttachment === previewAttachment)
      const item = idx >= 0 ? this.drawingList[idx] : { previewAttachment }
      const targetIndex = idx >= 0 ? idx : 0
      this.handleClick(item, targetIndex)
    }
  },
  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.back()
      } else {
        window.close()
      }
    },
    handlePrev() {
      if (this.activeIndex <= 0) return
      const index = this.activeIndex - 1
      this.handleClick(this.drawingList[index], index, true)
    },
    handleNext() {
      if (this.activeIndex >= this.drawingList.length - 1) return
      const index = this.activeIndex < 0 ? 0 : this.activeIndex + 1
      this.handleClick(this.drawingList[index], index, true)
    },
    handleClick(item, index, autoFit) {
      this.autoFit = !!autoFit
      this.activeIndex = index
      this.$nextTick(() => {
        const activeEl = this.$refs.drawingList?.querySelector('.drawing-item.active')
        if (activeEl) activeEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
      const id = item.previewAttachment
      if (!id) {
        this.pdfUrl = ''
        this.error = '该图纸缺少附件信息'
        return
      }
      this.loading = true
      this.error = null
      this.pdfUrl = ''
      queryFileById(id).then(res => {
        if (res?.completePath) {
          this.pdfUrl = res.completePath
        } else {
          this.error = '未获取到PDF文件地址'
        }
      }).catch(err => {
        this.error = 'PDF文件加载失败：' + (err?.message || '未知错误')
      }).finally(() => {
        this.loading = false
      })
    },
  },
}
</script>

<style lang="less" scoped>
.pdf-preview-list-page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  background: #fff;
  display: flex;
  flex-direction: column;
}
.pdf-preview-header {
  display: flex;
  align-items: center;
  padding: 8px 16px;
  border-bottom: 1px solid #e8e8e8;
  background: #fafafa;
  flex-shrink: 0;
}
.back-btn {
  font-size: 14px;
  margin-right: 12px;
}
.pdf-title {
  font-size: 16px;
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.pdf-preview-body {
  flex: 1;
  overflow: hidden;
  display: flex;
}
.drawing-panel {
  width: 240px;
  min-width: 240px;
  border-left: 1px solid #e8e8e8;
  background: #fafafa;
  display: flex;
  flex-direction: column;
}
.drawing-panel-title {
  padding: 10px 16px;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 1px solid #e8e8e8;
  background: #fff;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.page-nav {
  display: flex;
  align-items: center;
  gap: 4px;
}
.drawing-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}
.drawing-item {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  margin-bottom: 6px;
  border: 1px solid #e8e8e8;
  border-radius: 4px;
  cursor: pointer;
  background: #fff;
  transition: all 0.2s;

  &:hover {
    border-color: #91d5ff;
    background: #e6f7ff;
  }

  &.active {
    border-color: #1890ff;
    background: #e6f7ff;
  }
}
.drawing-icon {
  color: #1890ff;
  margin-right: 8px;
  flex-shrink: 0;
}
.drawing-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
}
.main-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.loading-container,
.error-container,
.empty-container {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}
.error-container {
  padding: 20px;
}
.preview-content {
  flex: 1;
  overflow: hidden;
  display: flex;
}
</style>
